import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { clearDirectoryCache } from "../api/directory";

const STORAGE_KEY = "kp_auth_v1";

export type AuthUser = {
  id: string;
  tenant_id: string;
  tenant_display_name?: string;
  is_platform_owner?: boolean;
  enabled_modules?: string[];
  role: "tenant_admin" | "manager" | "office_staff" | "instructor" | "student";
  branch_id?: string | null;
  instructor_id?: string | null;
  student_id?: string | null;
  email?: string;
  full_name?: string;
  password_reset_required?: boolean;
  last_password_change_at?: string | null;
};

export type AuthSession = {
  token: string;
  user: AuthUser;
};

type AuthContextValue = {
  session: AuthSession | null;
  isAuthenticated: boolean;
  setSession: (session: AuthSession | null) => void;
  updateUser: (patch: Partial<AuthUser>) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed?.token || !parsed?.user) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<AuthSession | null>(() => readStoredSession());

  const setSession = useCallback((next: AuthSession | null) => {
    clearDirectoryCache();
    setSessionState(next);
    if (next) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const updateUser = useCallback((patch: Partial<AuthUser>) => {
    setSessionState((prev) => {
      if (!prev) return prev;
      const nextSession: AuthSession = { ...prev, user: { ...prev.user, ...patch } };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
      } catch {
        // ignore storage errors
      }
      return nextSession;
    });
  }, []);

  const logout = useCallback(() => {
    clearDirectoryCache();
    setSession(null);
  }, [setSession]);

  useEffect(() => {
    if (!session?.token || session.user.tenant_display_name) return;
    let cancelled = false;
    api
      .get<{ user: Partial<AuthUser> }>("/auth/me")
      .then((res) => {
        if (cancelled || !res.user) return;
        updateUser(res.user);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [session?.token, session?.user?.tenant_display_name, updateUser]);

  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        setSessionState(readStoredSession());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: Boolean(session?.token),
      setSession,
      updateUser,
      logout,
    }),
    [session, setSession, updateUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function getStoredToken(): string | null {
  return readStoredSession()?.token ?? null;
}
